import { useIntl } from 'react-intl';
import { Helmet } from 'react-helmet';
import { Button, Col, Divider, Row, Dropdown, Card } from 'antd';
import { FaInfoCircle } from 'react-icons/fa';
import { useWorkOrderItems } from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.hooks';
import { useUpdateBreadcrumbParam } from '@/contexts/BreadcrumbContextProvider';
import { PageProps } from '@/types/PageProps';
import { Group } from '@/components/_common';
import WorkOrderComplementTabs from '@/components/Movements/WorkOrders/Details/Complements/WorkOrderComplementTabs';
import WorkOrderItemsTotalDiscounts from '@/components/Movements/WorkOrders/Details/Items/WorkOrderItemsTotalDiscounts';
import WorkOrderItemsSummary from '@/components/Movements/WorkOrders/Details/Items/WorkOrderItemsSummary';
import ComplementHeader from '@/components/Movements/WorkOrders/Details/Complements/ComplementHeader';
import { useWorkorderDetailContext } from '@/components/Movements/WorkOrders/Details/WorkorderDetailProvider';
import WorkOrderFooter from '@/components/Movements/WorkOrders/Details/WorkOrderFooter';

import './styles.less';
import WorkorderTabExtraAction from '@/components/Movements/WorkOrders/Details/WorkorderTabExtraAction';
import { useUpdateItemsWorkOrderComplement } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.hooks';
import { WorkOrderComplementStatus } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import workOrderValidationTabs from '@/utils/functions/workOrderValidationTabs';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { useWorkorderForms } from '@/components/Movements/WorkOrders/Details/WorkOrderFormProvider';
import WorkOrderItemRestoreItems from '@/components/Movements/WorkOrders/Details/Items/WorkOrderItemRestoreItems';
import WOM from '@/components/Movements/WorkOrders/Details/WOM';
import WorkOrderAuthorizedItems from '@/components/Movements/WorkOrders/Details/Items/WorkOrderAuthorizedItems';
import BasketItemsIntegrationVerification from '@/components/Movements/WorkOrders/Details/PecaAi/BasketItemsIntegrationVerification';
import WorkOrderItemList from '@/components/Movements/WorkOrders/Details/Items/WorkOrderItemList';
import ComplementPolicyBenefits from '@/components/Movements/WorkOrders/Details/Complements/ComplementPolicyBenefits';
import { isAppPortocar } from '@/index';
import WorkOrderServiceDiscountConfig from '@/components/Movements/WorkOrders/Details/Items/WorkOrderServiceDiscountConfig';
import WorkOrderItemAddButton from '@/components/Movements/WorkOrders/Details/Items/WorkOrderItemAddButton';
import WorkOrderItemActionsModal from '@/components/Movements/WorkOrders/Details/Items/WorkOrderItemActions';
import PecaAiProvider from '@/components/Movements/WorkOrders/Details/PecaAi/PecaAiProvider';
import ZfGenerateTokenSSO from '@/components/Movements/WorkOrders/Details/ZF/GenerateTokenSSO';

function WorkOrderPartsAndTasksPage({ route }: PageProps) {
  const intl = useIntl();

  const {
    workorder: { data: workOrder, isLoading: isLoadingWorkOrder },
    complement: {
      data: complement,
      isLoading: isLoadingComplement,
      complementIdentifier,
    },
    company: { companySettings },
  } = useWorkorderDetailContext();

  const isAuthorized =
    complement?.status === WorkOrderComplementStatus.Authorized;
  const isAuthorizedToAccessPecaAi =
    !!companySettings?.isAuthorizedToAccessPecaAi;

  const { hasFormChange } = useWorkorderForms();

  const workOrderItemList = useWorkOrderItems(
    complement?.id,
    undefined,
    undefined,
    false,
  );

  const workOrderItemsData = workOrderItemList.data;

  const updateItems = useUpdateItemsWorkOrderComplement();

  const displayNumber = `${workOrder?.number
    .toString()
    .padStart(6, '0')}/${complement?.number.toString().padStart(2, '0')}`;

  useUpdateBreadcrumbParam(':workOrderId', displayNumber);

  const refetch = () => {
    workOrderItemList.refetch();
  };

  const infoContent = (
    <Card bodyStyle={{ padding: 20 }} style={{ borderRadius: 5 }}>
      <Group gap={10} style={{ marginBottom: 20 }}>
        <div className="item-status-tag" style={{ backgroundColor: 'red' }} />
        <span>
          {intl.formatMessage({
            id: `workOrders.domain.enums.work_order_item_status.red`,
          })}
        </span>
      </Group>

      <Group gap={10} style={{ marginBottom: 20 }}>
        <div
          className="item-status-tag"
          style={{ backgroundColor: 'yellow' }}
        />
        <span>
          {intl.formatMessage({
            id: `workOrders.domain.enums.work_order_item_status.yellow`,
          })}
        </span>
      </Group>

      <Group gap={10} style={{ marginBottom: 20 }}>
        <div
          className="item-status-tag"
          style={{ backgroundColor: 'orange' }}
        />
        <span>
          {intl.formatMessage({
            id: `workOrders.domain.enums.work_order_item_status.orange`,
          })}
        </span>
      </Group>

      <Group gap={10} style={{ marginBottom: 20 }}>
        <div className="item-status-tag" style={{ backgroundColor: 'blue' }} />
        <span>
          {intl.formatMessage({
            id: `workOrders.domain.enums.work_order_item_status.blue`,
          })}
        </span>
      </Group>

      <Group gap={10} style={{ marginBottom: 20 }}>
        <div className="item-status-tag" style={{ backgroundColor: 'green' }} />
        <span>
          {intl.formatMessage({
            id: `workOrders.domain.enums.work_order_item_status.green`,
          })}
        </span>
      </Group>

      <Group gap={10}>
        <div className="item-status-tag" style={{ backgroundColor: 'black' }} />
        <span>
          {intl.formatMessage({
            id: `workOrders.domain.enums.work_order_item_status.black`,
          })}
        </span>
      </Group>
    </Card>
  );

  async function handleUpdateItems() {
    if (workOrder && complement) {
      await updateItems.mutateAsync({
        workorderId: workOrder.id,
        complementId: complement.id,
      });
    }
  }

  return (
    <>
      <Helmet
        title={`${
          complementIdentifier ? `${complementIdentifier} | ` : ''
        }${intl.formatMessage({
          id: route.name,
        })}`}
      />

      <WorkOrderComplementTabs
        permissions={route.permissions}
        route={workOrderValidationTabs(
          route,
          complement?.type,
          complement?.status,
        )}
        isLoading={isLoadingWorkOrder || isLoadingComplement}
        extra={<WorkorderTabExtraAction />}
      >
        <ComplementHeader />

        <Row
          justify="space-between"
          gutter={[24, 24]}
          style={{ marginBottom: 24 }}
        >
          <Col span={24} md={12}>
            <Group gap={10}>
              <h3 style={{ margin: 0 }}>Itens</h3>

              <Group gap={16}>
                <Dropdown overlay={infoContent}>
                  <FaInfoCircle size={20} />
                </Dropdown>

                <WorkOrderItemActionsModal />
              </Group>
            </Group>
          </Col>

          <Col span={24} md={12}>
            <SpaceForButton justifyContent="end">
              <WorkOrderItemRestoreItems />

              {!!workOrderItemsData?.data.length && isAuthorized && (
                <Button
                  block
                  onClick={handleUpdateItems}
                  loading={updateItems.isLoading}
                >
                  Atualizar itens
                </Button>
              )}

              <WorkOrderItemAddButton
                workOrderItemListRefresh={workOrderItemList.refetch}
              />

              <WOM />

              {!isAppPortocar && isAuthorizedToAccessPecaAi && (
                <PecaAiProvider />
              )}

              <ZfGenerateTokenSSO />
            </SpaceForButton>
          </Col>
        </Row>

        <WorkOrderServiceDiscountConfig />

        <WorkOrderItemList
          workOrderItems={workOrderItemsData?.data}
          loading={workOrderItemList.isFetching}
          refetchItems={workOrderItemList.refetch}
        />

        <BasketItemsIntegrationVerification
          workorderItems={workOrderItemsData?.data}
        />

        <WorkOrderAuthorizedItems />

        <Row style={{ marginTop: 36 }} justify="space-between">
          {!!complement && (
            <Col span={24} lg={12}>
              <WorkOrderItemsTotalDiscounts
                complementId={complement?.id}
                totalDiscountPart={workOrderItemsData?.totalDiscountPart}
                totalDiscountService={workOrderItemsData?.totalDiscountService}
                maxDiscountPart={workOrderItemsData?.partTotal}
                maxDiscountService={workOrderItemsData?.serviceTotal}
                refetch={refetch}
              />
            </Col>
          )}

          <Col
            span={24}
            lg={12}
            style={{ display: 'flex', justifyContent: 'end' }}
          >
            <WorkOrderItemsSummary
              partTotal={workOrderItemsData?.partTotal}
              serviceTotal={workOrderItemsData?.serviceTotal}
              total={workOrderItemsData?.total}
              totalDiscountPart={workOrderItemsData?.totalDiscountPart}
              totalDiscountPartPercent={
                workOrderItemsData?.totalDiscountPartPercent
              }
              totalDiscountService={workOrderItemsData?.totalDiscountService}
              totalDiscountServicePercent={
                workOrderItemsData?.totalDiscountServicePercent
              }
            />
          </Col>
        </Row>

        {isAppPortocar && <ComplementPolicyBenefits />}

        <Divider />

        <WorkOrderFooter disabled={!hasFormChange} />
      </WorkOrderComplementTabs>
    </>
  );
}

export default WorkOrderPartsAndTasksPage;
